import { CSSProperties } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useBrand from 'Hooks/useBrand'
import ModalSymphony from 'elements/ModalSymphony'
import InputSymphony from 'elements/InputSymphony'
import ButtonSymphony from 'elements/ButtonSymphony'

export interface SignupFlowConfig {
    /** Modal title */
    title: string
    /** Modal subtitle */
    subtitle: string
    /** Custom content to show in the modal (optional) */
    customContent?: React.ReactNode
    /** Additional data to store in localStorage for restoration */
    additionalStorageData?: Record<string, string>
    /** Key prefix for localStorage items (to avoid conflicts) */
    storageKeyPrefix?: string
}

export interface UseSignupFlowProps {
    /** Configuration for the signup modal */
    config: SignupFlowConfig
    /** Chat ID for restoration */
    chatId?: string
    /** Temporary session ID for restoration */
    temporarySessionId?: string
}

export const useSignupFlow = ({ config, chatId, temporarySessionId }: UseSignupFlowProps) => {
    const { loginWithRedirect } = useAuth0()
    const { 
        anonymousBrandEmail, 
        setAnonymousBrandEmail, 
        linkEmailToAnonymousBrand,
        currentBrand 
    } = useBrand()

    const saveEmailAndRedirectToCheckout = async () => {
        if (!anonymousBrandEmail.trim()) {
            console.error('No email provided')
            return
        }

        try {
            // Step 1: Link email to the anonymous brand
            if (currentBrand?.id) {
                await linkEmailToAnonymousBrand(anonymousBrandEmail.trim())
            }

            // Step 2: Store session data in localStorage for post-auth restoration
            if (temporarySessionId) {
                localStorage.setItem('pre-auth-session-id', temporarySessionId)
            }

            if (chatId) {
                localStorage.setItem('pre-auth-chat-id', chatId)
            }

            // Step 3: Store any additional workspace-specific data
            if (config.additionalStorageData) {
                Object.entries(config.additionalStorageData).forEach(([key, value]) => {
                    const storageKey = config.storageKeyPrefix ? `${config.storageKeyPrefix}-${key}` : key
                    localStorage.setItem(storageKey, value)
                })
            }

            // Step 4: Set email in state for local use
            setAnonymousBrandEmail(anonymousBrandEmail.trim())

            // Step 5: Redirect to Auth0 with email hint
            await loginWithRedirect({
                authorizationParams: {
                    login_hint: anonymousBrandEmail.trim(),
                    screen_hint: 'signup'
                },
                appState: {
                    returnTo: window.location.pathname + window.location.search
                }
            })
        } catch (error) {
            console.error('Failed to link email and redirect:', error)
        }
    }

    const renderSignupModal = (isOpen: boolean, onClose: () => void) => {
        return (
            <ModalSymphony
                className="signupFlowModal"
                isOpen={isOpen}
                onClose={onClose}
                size="medium"
                title={config.title}
                subtitle={config.subtitle}
            >
                {config.customContent}
                <InputSymphony
                    className="signupFlowEmailInput"
                    label="Email"
                    placeholder="Enter your email"
                    value={anonymousBrandEmail}
                    onChange={(value) => setAnonymousBrandEmail(value)}
                />
                <div style={sty.signupModalActions}>
                    <ButtonSymphony
                        className="signupFlowSignupButton"
                        variant="primary"
                        onClick={saveEmailAndRedirectToCheckout}
                        disabled={!anonymousBrandEmail.trim()}
                    >
                        Sign Up
                    </ButtonSymphony>
                </div>
            </ModalSymphony>
        )
    }

    return {
        saveEmailAndRedirectToCheckout,
        renderSignupModal,
        anonymousBrandEmail,
        setAnonymousBrandEmail
    }
}

const sty: Record<string, CSSProperties> = {
    signupModalActions: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 24,
    },
} 